<script>
// Components
import Layout from "@/router/layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config.json";
import UserProfile from "@/router/views/users/user-profile"

// Helpers
import DateUtils from '@/helpers/date';
import UserUtils from '@/helpers/users';
import Storage from "@/helpers/storage"

// Services
import ReviewsService from "@/services/ReviewsService";
import UsersDataService from "@/services/UsersDataService"

// External Components
import DatePicker  from "vue2-datepicker";
import 'vue2-datepicker/index.css';
import {VueTabs, VTab} from 'vue-nav-tabs'
import 'vue-nav-tabs/themes/vue-tabs.css';
import CoolLightBox from 'vue-cool-lightbox';
import 'vue-cool-lightbox/dist/vue-cool-lightbox.min.css';

// Lib
import isEmpty from "is-empty";

export default {
  name: "reviews",
  page: {
    localePicker: () => import(`vue2-datepicker/locale/${this.$i18n.locale}`),
    title: "Reviews Analytics",
    meta: [
      {
        name: "description",
        content: appConfig.description
      },
    ],
  },

  components: {
    Layout,
    PageHeader,
    DatePicker,
    VueTabs,
    VTab,
    UserProfile,
    CoolLightBox,
  },

  methods: {
    ...UserUtils,
    ...DateUtils,

    handlePageChange(value) {
      this.page = value;
      if (this.reviewsSearch === 'filters')
        this.loadReviews();
      else if (this.reviewsSearch === 'search')
        this.processSearch(false);
    },

    handleDisplayChange() {
      this.loadReviews();
    },

    handleDateChange() {
      let now = new this.$moment();
      switch (this.filters.dates.selected) {
        case "always":
          this.filters.fromDate = new this.$moment('2023-01-01').format('YYYY-MM-DD');
          this.filters.toDate = now.format('YYYY-MM-DD');
          break;
        case "today":
          this.filters.fromDate = now.format('YYYY-MM-DD');
          this.filters.toDate = now.format('YYYY-MM-DD');
          break;
        case "yesterday":
          this.filters.fromDate = now.subtract(1, 'day').format('YYYY-MM-DD');
          this.filters.toDate = this.filters.fromDate;
          break;
        case "week":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.subtract(1, 'week').startOf('day').format('YYYY-MM-DD');
          break;
        case "curMonth":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.startOf('month').format('YYYY-MM-DD');
          break;
        case "last30Day":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.subtract(30, 'day').startOf('day').format('YYYY-MM-DD');
          break;
        case "lastMonth":
          this.filters.fromDate = now.subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
          this.filters.toDate = now.endOf('month').format('YYYY-MM-DD');
          break;
        case "last3Month":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.subtract(3, 'month').startOf('day').format('YYYY-MM-DD');
          break;
        case "curYear":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.startOf('year').format('YYYY-MM-DD');
          break;
        case "custom":
          this.filters.fromDate = null;
          this.filters.toDate = null;
          break;
      }
    },

    getRequestParams() {
      const params = {};

      params["page"] = this.page - 1;
      params["size"] = this.pageSizes.selected;

      if (this.filters.dates.selected === 'custom' && this.filters.dates.dateRange !== null) {
        this.filters.fromDate = this.$moment(this.filters.dates.dateRange[0]).format("YYYY-MM-DD 00:00:00");
        this.filters.toDate = this.$moment(this.filters.dates.dateRange[1]).format("YYYY-MM-DD 23:59:59");
      } else if (this.filters.fromDate === null)
        this.handleDateChange();

      if (this.filters.toDate !== null)
        params['toDate'] = this.filters.toDate;

      if (this.filters.fromDate !== null)
        params['fromDate'] = this.filters.fromDate;

      params['versions'] = this.filters.versions.selected;
      params['gender'] = this.filters.genders.selected;
      params['status'] = this.filters.status.selected;
      params['countries'] = this.filters.countries.selected;
      params['notes'] = this.filters.notes.selected;
      params['operator'] = this.filters.notes_operator.selected;
      if (this.filters.hasComment === true)
        params['comment'] = true;

      if (this.$store.state.layout.timezone === 'utc') {
        params['tz'] = this.$moment.tz.guess();
      }

      return params;
    },

    loadStats() {
      const params = this.getRequestParams();
      ReviewsService
          .getStats(params)
          .then(response => {

            const {total, historyGraph} = response.data;
            this.stats.displayed = total.displayed;
            this.stats.reviewed = total.reviewed;
            this.stats.reviewed_perc = total.reviewed_perc;
            this.stats.store = total.store;
            this.stats.store_perc = total.store_perc;
            this.stats.note = total.note;
            this.stats.note_count = total.note_count;
            this.stats.note_man = total.note_man;
            this.stats.note_woman = total.note_woman;

            this.$refs.reviewsNoteHistoryChart.updateSeries([{
              name: 'Note',
              type: 'area',
              data: historyGraph.note
            }]);

            this.$refs.reviewsHistoryChart.updateSeries([{
              name: "Displayed",
              type: 'bar',
              data: historyGraph.displayed
            }, {
              name: "Reviewed",
              type: 'bar',
              data: historyGraph.reviewed
            }, {
              name: 'Store',
              type: 'bar',
              data: historyGraph.store
            }]);
          })
          .catch(error => {
            window.console.error(error);
          });
    },

    loadReviews() {
      this.isLoading = true;
      const params = this.getRequestParams();
      this.reviewsSearch = 'filters';
      ReviewsService
          .getList(params)
          .then(response => {
            const {items, totalItems} = response.data;
            this.reviews = items;
            this.count = totalItems;
            this.getMediaThumb();
          })
          .catch(error => {
            window.console.error(error);
            this.$toast.error(this.$t("reviews.errorReviewsNoResult"));
          })
          .finally(() => {
            this.isLoading = false;
          });
    },

    resetData() {
      this.reviews = [];
      this.stats = {
        displayed: '-',
        reviewed: '-',
        reviewed_perc: '-',
        store: '-',
        store_perc: '-',
        note: '-',
        note_count: '-',
        note_man: '-',
        note_woman: '-',
      };
    },

    getFilters() {
      ReviewsService.getFilters()
          .then(response => {
            response.data.filters.versions.forEach(type => {
              this.filters.versions.options.push(type);
            });
          })
          .catch(error => {
            window.console.log(error);
            this.$toast.error(this.$t("reviews.getFiltersError"));
          });
    },

    processFilters() {
      this.resetData();
      this.loadReviews();
      this.loadStats();
    },

    processSearch(resetPage) {
      if (isEmpty(this.filters.searchType)) {
        this.$toast.error(this.$t("reviews.error_searchType"));
        return;
      }

      if (isEmpty(this.filters.searchData)) {
        this.$toast.error(this.$t("reviews.error_searchData"));
        return;
      }

      this.resetData();
      this.reviewsSearch = 'search';
      if (resetPage) {
        this.page = 1;
      }

      this.isLoading = true;
      const params = {
        type: this.filters.searchType.selected,
        data: this.filters.searchData,
        page: this.page - 1,
        size: this.pageSizes.selected,
      };

      ReviewsService
          .search(params)
          .then(response => {
            const {items, totalItems} = response.data;
            this.reviews = items;
            this.count = totalItems;
            this.isLoading = false;
            this.getMediaThumb();
          })
          .catch(error => {
            window.console.error(error);
            this.$toast.error(this.$t("reviews.errorReviewsNoResult"));
            this.isLoading = false;
          });
    },

    getMediaThumb() {
      if (this.reviews.length > 0)
        this.reviews.forEach(review => {
          if (review.photo && review.photo.length > 0)
            Storage.getUserMedia(review.userId, review.photo, 'review-user-' + review.reviewId);

        });
    },

    _isEmpty(val) {
      return isEmpty(val);
    },

    searchUser(userId) {
      this.filters.searchType.selected = 'userId';
      this.filters.searchData = userId;
      this.processSearch(true);
    },

    showContextMenu(event, user) {
      this.$refs.reviewsContextMenu.showMenu(event, user)
    },

    processContextMenu(event) {
      const {item, option} = event;
      switch (option.value) {
        case "show":
          this.showUser(item.userId);
          break;
        case "search":
          this.searchUser(item.userId);
          break;
      }
    },

    /* User profile */
    openUserPane() {
      if (!this.isUserPaneDisplayed)
        this.isUserPaneDisplayed = true;
    },

    closeUserPane() {
      this.userTabsActive = '';
      this.isUserPaneDisplayed = false;
    },

    async showUser(userId) {
      const isUserAlreadyDisplayed = this.userTabs.findIndex(x => x.userId === userId);
      let forceShow = false;
      if (isUserAlreadyDisplayed === -1) {
        try {
          const result = await UsersDataService.get(userId);
          if (typeof result.data === 'undefined')
            throw new Error('unable to find user ' + userId);

          this.userTabs.push(UserUtils.normalize(result.data));
          forceShow = true;
        } catch (error) {
          window.console.error(error);
          this.$toast.error(this.$t("global_error"));
        }
      }

      this.openUserPane();

      if (forceShow || (!forceShow && this.userTabsActive !== userId)) {
        this.userTabsActive = '';
        setTimeout(() => {
          this.userTabsActive = userId;
        }, 100);
      }
    },

    showUserMedia(media, index) {
      this.userMedia.media = media;
      this.userMedia.index = index;
    },

    removeUserTab(userId) {
      const index = this.userTabs.findIndex(x => x.userId === userId);
      if (index > -1)
        this.userTabs.splice(index, 1);

      if (this.userTabs.length === 0)
        this.closeUserPane();
    }
  },

  mounted() {
    this.getFilters();
    this.processFilters();
  },

  data() {
    return {
      isLoading: false,
      title: this.$t("menuitems.dashboards.reviews"),
      reviewsSearch: 'filters',
      reviews: [],
      page: 1,
      count: 0,
      pageSizes: {
        options: ['20', '50', '100'],
        selected: '20'
      },
      optionsContext: [
        {
          name: this.$t("show_user"),
          value: 'show'
        },
        {
          name: this.$t("search_user"),
          value: 'search'
        },
      ],
      stats: {
        displayed: '-',
        reviewed: '-',
        reviewed_perc: '-',
        store_perc: '-',
        store: '-',
        note: '-',
        note_count: '-',
        note_man: '-',
        note_woman: '-',
      },
      historyNoteGraph: {
        series: [],
        chartOptions: {
          chart: {
            stacked: false,
            type: 'area',
            toolbar: {
              show: true,
            },
            zoom: {
              enabled: true,
            },
          },

          dataLabels: {
            enabled: false
          },
          stroke: {
            curve: 'straight',
            width: 2,
          },
          xaxis: {
            type: 'datetime',
            labels: {
              show: true,
              style: {
                fontSize: 11
              },
              hideOverlappingLabels: true
            }
          },
          yaxis: [{
            axisTicks: {
              show: true,
            },
            axisBorder: {
              show: true,
              color: '#666666'
            },
            labels: {
              style: {
                colors: '#666666',
              }
            },
            title: {
              text: "Note",
              style: {
                color: '#666666',
              }
            },
            tooltip: {
              enabled: true
            },
            min: 0.0,
            max: 6.0
          }]
        }
      },

      historyGraph: {
        series: [],
        chartOptions: {
          chart: {
            stacked: true,
            type: 'bar',
            toolbar: {
              show: true,
            },
            zoom: {
              enabled: true,
            },
          },
          plotOptions: {
            bar: {
              horizontal: false,
              endingShape: "rounded",
              borderRadius: 10,
              dataLabels: {
                total: {
                  enabled: false,
                  style: {
                    fontSize: '13px',
                  }
                }
              }
            },
          },
          dataLabels: {
            enabled: false,
          },
          tooltip: {
            shared: true,
            intersect: false
          },
          xaxis: {
            type: 'datetime',
            labels: {
              show: true,
              style: {
                fontSize: 11
              },
              hideOverlappingLabels: true
            }
          },
          yaxis: [{
            axisTicks: {
              show: true,
            },
            axisBorder: {
              show: true,
              color: '#666666'
            },
            labels: {
              style: {
                colors: '#666666',
              }
            },
            title: {
              text: "Status",
              style: {
                color: '#666666',
              }
            },
            tooltip: {
              enabled: true
            }
          }],
          colors: ["#f1b44c", "#095bc5", "#34c38f"],
          legend: {
            position: "bottom",
          },
          fill: {
            opacity: 1,
          },
        },
      },
      filters: {
        searchType: {
          selected: 'userId',
          options: [
            {value:'userId', text:this.$t("userId")},
            {value:'email', text:this.$t("email")},
          ]
        },
        searchData: "",
        fromDate: null,
        toDate: null,
        dates: {
          dateRange: null,
          selected: 'last3Month',
          options: [
            {value:'today', text:this.$t("dates.today")},
            {value:'yesterday', text:this.$t("dates.yesterday")},
            {value:'week', text:this.$t("dates.week")},
            {value:'curMonth', text:this.$t("dates.curMonth")},
            {value:'last30Day', text:this.$t("dates.last30Day")},
            {value:'lastMonth', text:this.$t("dates.lastMonth")},
            {value:'last3Month', text:this.$t("dates.last3Month")},
            {value:'curYear', text:this.$t("dates.curYear")},
            {value:'always', text:this.$t("dates.always")},
            {value:'custom', text:this.$t("dates.custom")}
          ]
        },
        versions: {
          selected: 'all',
          options: [
            {value: 'all', text: "All Versions"}
          ]
        },
        hasComment: false,
        countries: {
          selected: 'all',
          options: [
            {value: 'all', text: 'All Countries'},
            {value: '', text: '----------------'},
            {value: 'FR', text: 'FR - France'},
            {value: 'BE', text: 'BE - Belgium'},
            {value: 'CH', text: 'CH - Switzerland'},
            {value: 'CA', text: 'CA - Canada'},
            {value: '', text: '----------------'},
            {value: 'others', text: 'Others'},
          ]
        },
        genders: {
          selected: 'all',
          options: [
            {value: 'all', text: this.$t("users.all_genders")},
            {value: 1, text: this.$t("users.search.genderMale")},
            {value: 2, text: this.$t("users.search.genderFemale")}
          ]
        },
        notes: {
          selected: 'all',
          options: [
            {value: 'all', text: "All Notes"},
            {value: '5', text: "5"},
            {value: '4', text: "4"},
            {value: '3', text: "3"},
            {value: '2', text: "2"},
            {value: '1', text: "1"},
            {value: '0', text: "0"},
          ]
        },
        notes_operator: {
          selected: 'e',
          options: [
            {value: 'e', text: "="},
            {value: 'g', text: ">"},
            {value: 'ge', text: ">="},
            {value: 'l', text: "<"},
            {value: 'le', text: "<="},
            {value: 'ne', text: "!="},
          ]
        },
        status: {
          selected: 'all',
          options: [
            {value: 'all', text: this.$t("reviews.status_all")},
            {value: 'N', text: this.$t("reviews.with_note")},
            {value: '', text: '----------------'},
            {value: 'D', text: this.$t("reviews.status_D")},
            {value: 'R', text: this.$t("reviews.status_R")},
            {value: 'S', text: this.$t("reviews.status_S")},
          ]
        },
        ageMin: 0,
        ageMax: 0,
      },

      /* User profile */
      isUserPaneDisplayed: false,
      userTabs:[],
      userTabsActive: '',
      userMedia: {
        media: [],
        index: -1
      },
    }
  }
}

</script>

<template>
  <Layout>
    <PageHeader :title="title" />
    <div class="position-relative">
      <b-button
          class="position-fixed"
          style="top: 77px; right: 15px;z-index:150"
          v-show="!isUserPaneDisplayed && userTabs.length > 0"
          @click="openUserPane"
          variant="outline-primary">
        <i class="bx bxs-cog"></i> Show Panel
      </b-button>

      <!-- Filters -->
      <div class="card">
        <div class="card-body">
          <!-- Search -->
          <b-form inline class="row gy-2 gx-3 align-items-center" id="reviewsSearchForm" @submit.stop.prevent>
            <div class="col-sm-auto">
              <b-form-select
                  id="reviewsSearchTypeFilter"
                  v-model="filters.searchType.selected"
                  class="form-select me-lg-1"
                  :options="filters.searchType.options">
              </b-form-select>
            </div>
            <div class="col-sm-auto">
              <b-form-input
                  id="reviewsSearchData"
                  class="me-lg-1"
                  v-model="filters.searchData"
                  placeholder="Search…">
              </b-form-input>
            </div>
            <div class="col-sm-auto">
              <b-button variant="primary" class="w-md" @click="processSearch(true)">{{ $t("searchBtn") }}</b-button>
            </div>
          </b-form>
          <hr />
          <!-- filters -->
          <b-form inline class="row gy-2 gx-3 align-items-center" id="reviewsFiltersForm" @submit.stop.prevent>

            <!-- dates -->
            <div class="col-sm-auto d-flex">
              <b-form-select
                  id="reviewsDatesFilter"
                  @change="handleDateChange"
                  v-model="filters.dates.selected"
                  class="form-select me-lg-1"
                  :options="filters.dates.options">
              </b-form-select>
              <date-picker
                  v-show="filters.dates.selected === 'custom'"
                  :lang="page.localePicker"
                  :placeholder="this.$t('choose_date_interval')"
                  type="date"
                  value-type="format"
                  show-week-number
                  v-model="filters.dates.dateRange"
                  class="me-lg-2"
                  range append-to-body ></date-picker>

            </div>

            <!-- Countries -->
            <div class="col-sm-auto">
              <b-form-select
                  id="reviewsCountriesFilter"
                  v-model="filters.countries.selected"
                  class="form-select me-lg-1"
                  :options="filters.countries.options">
              </b-form-select>
            </div>

            <!-- Genders -->
            <div class="col-sm-auto">
              <b-form-select
                  id="reviewsGenderFilter"
                  v-model="filters.genders.selected"
                  class="form-select me-lg-1"
              >
                <option v-for="ct in filters.genders.options" :key="ct.value" :value="ct.value">{{ ct.text }}</option>
              </b-form-select>
            </div>

            <!-- Status -->
            <div class="col-sm-auto">
              <b-form-select
                  id="reviewsStatusFilter"
                  v-model="filters.status.selected"
                  class="form-select me-lg-1"
              >
                <option v-for="st in filters.status.options" :key="st.value" :value="st.value">{{ st.text }}</option>
              </b-form-select>
            </div>

            <!-- Versions -->
            <div class="col-sm-auto">
              <b-form-select
                  v-model="filters.versions.selected"
                  class="form-select me-lg-1"
              >
                <option v-for="st in filters.versions.options" :key="st.value" :value="st.value">{{ st.text }}</option>
              </b-form-select>
            </div>

            <!-- Notes -->
            <div class="col-sm-auto">
              <b-form-select
                  v-model="filters.notes_operator.selected"
                  class="form-select me-lg-1"
              >
                <option v-for="st in filters.notes_operator.options" :key="st.value" :value="st.value">{{ st.text }}</option>
              </b-form-select>
            </div>
            <div class="col-sm-auto">
              <b-form-select
                  id="reviewsNoteFilter"
                  v-model="filters.notes.selected"
                  class="form-select me-lg-1"
              >
                <option v-for="st in filters.notes.options" :key="st.value" :value="st.value">{{ st.text }}</option>
              </b-form-select>
            </div>

            <!-- Comment -->
            <div class="col-sm-auto">
              <b-form-checkbox
                  v-model="filters.hasComment"
                  id="filter-hascomment"
                  class="form-check"
              >{{ $t("reviews.hasComment") }}</b-form-checkbox>
            </div>

            <div class="col-sm-auto">
              <b-button variant="primary" class="w-md" @click="processFilters">{{ $t("find") }}</b-button>
            </div>
          </b-form>
        </div>
      </div>

      <!-- Loader -->
      <div class="row d-flex justify-content-center">
        <vue-ellipse-progress
            v-show="isLoading"
            :loading="isLoading"
            :angle="-90"
            color="#50a5f1"
            emptyColor="#999"
            emptyColorFill="#f8f8fb"
            :size="100"
            :thickness="3"
            :emptyThickness="2"
            lineMode="out 2"
            animation="bounce 700 400"
            fontColor="#666"
            :half="false"
            class="mt-5 mb-5"
            fontSize="1rem">
        </vue-ellipse-progress>
      </div>

      <div class="row" v-if="reviewsSearch === 'filters'">

        <!-- Stats Plain -->
        <div class="col-12 col-lg-3">
          <!-- Note -->
          <div class="card mini-stats-wid">
            <div class="card-body">
              <div class="media">
                <div class="media-body">
                  <p class="text-dark fw-medium mb-2">{{ $t("reviews.note") }}</p>
                  <h4 class="mb-0">{{ stats.note }} <span class="fs-5 text-info">{{ stats.note_man }}% -</span><span class="fs-5 text-pink"> {{ stats.note_woman }}%</span></h4>
                  <small>{{ stats.note_count }} {{ $t("reviews.notes") }}</small>
                </div>

                <div class="avatar-sm align-self-center mini-stat-icon rounded-circle ">
                <span class="avatar-title bg-secondary">
                  <i class="bx bx-archive-in font-size-24"></i>
                </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-3">
          <!-- Displayed -->
          <div class="card mini-stats-wid">
            <div class="card-body">
              <div class="media">
                <div class="media-body">
                  <p class="text-dark fw-medium mb-2">{{ $t("reviews.displayed") }}</p>
                  <h4 class="mb-0">{{ stats.displayed }}</h4>
                </div>

                <div class="avatar-sm align-self-center mini-stat-icon rounded-circle ">
                <span class="avatar-title bg-warning">
                  <i class="bx bx-archive-in font-size-24"></i>
                </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-3">
          <!-- Reviewed -->
          <div class="card mini-stats-wid">
            <div class="card-body">
              <div class="media">
                <div class="media-body">
                  <p class="text-muted fw-medium mb-2">{{ $t("reviews.reviewed") }}</p>
                  <h4 class="mb-0">{{ stats.reviewed }} <span class="fs-5 text-muted">{{ stats.reviewed_perc }}%</span></h4>
                </div>

                <div class="avatar-sm align-self-center mini-stat-icon rounded-circle ">
                <span class="avatar-title bg-info">
                  <i class="bx bx-error font-size-24"></i>
                </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-3">
          <!-- Store -->
          <div class="card mini-stats-wid">
            <div class="card-body">
              <div class="media">
                <div class="media-body">
                  <p class="text-dark fw-medium mb-2">{{ $t("reviews.store") }}</p>
                  <h4 class="mb-0">{{ stats.store }} <span class="fs-5 text-muted">{{ stats.store_perc }}%</span></h4>
                </div>

                <div class="avatar-sm align-self-center mini-stat-icon rounded-circle">
                <span class="avatar-title bg-success">
                  <i class="bx bx-minus-circle font-size-24"></i>
                </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row" v-if="reviewsSearch === 'filters'">
        <!-- History Send -->
        <div class="col-12 col-lg-6">
          <div class="card">
            <div class="card-body" id="cardChartreviewsHistory">
              <h5 class="card-title">Reviews History</h5>
              <apexchart
                  ref="reviewsHistoryChart"
                  class="apex-charts"
                  type="bar"
                  dir="ltr"
                  height="300"
                  :series="historyGraph.series"
                  :options="historyGraph.chartOptions"
              ></apexchart>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-6">
          <div class="card">
            <div class="card-body" id="cardChartnoteHistory">
              <h5 class="card-title">Note History</h5>
              <apexchart
                  ref="reviewsNoteHistoryChart"
                  class="apex-charts"
                  type="line"
                  dir="ltr"
                  height="300"
                  :series="historyNoteGraph.series"
                  :options="historyNoteGraph.chartOptions"
              ></apexchart>
            </div>
          </div>
        </div>

      </div>

      <!-- Top list tools -->
      <div class="row mb-3">
        <!-- Display settings -->
        <div class="col-lg-auto col-12">
          <b-select class="form-select d-inline w-auto m-1" v-model="pageSizes.selected" @change="handleDisplayChange">
            <option v-for="size in pageSizes.options" :key="size" :value="size">{{ size }} {{ $t('pageSize') }}</option>
          </b-select>

          {{ count }} {{ $t('resultsCount') }}
        </div>
        <!-- Pagination -->
        <div class="col-lg-auto col-12">
          <div class="m-1 pagination-rounded">
            <b-pagination
                v-model="page"
                :total-rows="count"
                :per-page="pageSizes.selected"
                first-number
                last-number
                @change="handlePageChange"
                aria-controls="reviews-list"
            ></b-pagination>
          </div>
        </div>
      </div>

      <!-- Result list -->
      <div class="row bg-white" v-if="!isLoading && reviews.length > 0">
        <div class="table-responsive">
          <table
              id="reviews-list"
              class="table project-list-table table-centered table-borderless align-middle"
          >
            <thead>
            <tr>
              <th scope="col"></th>
              <th scope="col">{{ $t('reviews.status') }}</th>
              <th scope="col">{{ $t('reviews.note') }}</th>
              <th scope="col">{{ $t('reviews.user') }}</th>
              <th scope="col">{{ $t('reviews.versions') }}</th>
              <th scope="col">{{ $t('reviews.store') }}</th>
              <th scope="col">{{ $t('reviews.comment') }}</th>
              <th scope="col">{{ $t('action') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="review in reviews" :key="review.reviewId" @contextmenu.prevent.stop="showContextMenu($event, review)">
              <td>
                <p class="text-muted font-size-12">{{ formatDateString(review.reviewDate, 'DD MMM YYYY HH:mm') || '-' }}</p>
              </td>
              <td>
                <span
                    class="badge"
                    :class="{
                      'bg-success text-light': `${review.status}` === 'S',
                      'bg-info text-light': `${review.status}` === 'R',
                      'bg-secondary text-light': `${review.status}` === 'D',
                    }"
                >{{ $t(`reviews.status_${review.status}`) }}</span>
              </td>
              <td>
                <p class="text-muted font-size-12"><strong>{{ review.note }}</strong></p>
              </td>
              <td>
                <div class="media">
                  <div class="avatar-xs">
                    <img
                        :id="`review-user-${review.reviewId}`"
                        :src="require('@/assets/images/users/default-user.png')"
                        class="d-flex rounded-circle avatar-xs" />
                  </div>
                  <div class="media-body ms-1">
                    <h5 class="font-size-14 mb-0">
                      <span
                          :class="{
                          'text-primary': review.sex === 1,
                          'text-danger': review.sex === 2,
                          'text-dark': review.sex === 0,
                        }"
                      >
                        {{ review.name || '-' }}
                      </span> {{ getAge(review.birth) }}</h5>
                    <small class="text-muted">#{{ review.userId }}</small>
                  </div>
                </div>
              </td>
              <td>
                <p class="text-muted font-size-12">{{ review.appVersionName }}<br><small>{{ review.appVersionCode }}</small></p>
              </td>
              <td>
                <p class="text-muted font-size-12">{{ review.storeDisplayCount }}</p>
              </td>
              <td>
                <p class="text-muted font-size-12">{{ review.reviewComment || "" }}</p>
              </td>
              <td>
                <b-dropdown
                    class="card-drop"
                    variant="white"
                    menu-class="dropdown-menu-end"
                    right
                    toggle-class="p-0"
                >
                  <template v-slot:button-content>
                    <i class="mdi mdi-dots-horizontal font-size-18"></i>
                  </template>
                  <b-dropdown-item
                      href="javascript: void(0);"
                      @click="showUser(review.userId)"
                  >{{ $t("show_user") }}</b-dropdown-item>
                  <b-dropdown-item
                      href="javascript: void(0);"
                      @click="searchUser(review.userId)"
                  >{{ $t("search_user") }}</b-dropdown-item>
                </b-dropdown>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Result Empty -->
      <div class="row" v-if="!isLoading && reviews.length === 0">
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <h4 class="card-title">Empty Result</h4>
              <p>No reviews were found for these filters or search. Try again.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- bottom list tools -->
      <div class="row mb-3">
        <!-- Display settings -->
        <div class="col-lg-auto col-12">
          <b-select class="form-select d-inline w-auto m-1" v-model="pageSizes.selected" @change="handleDisplayChange">
            <option v-for="size in pageSizes.options" :key="size" :value="size">{{ size }} {{ $t('pageSize') }}</option>
          </b-select>
        </div>
        <!-- Pagination -->
        <div class="col-lg-auto col-12">
          <div class="m-1 pagination-rounded">
            <b-pagination
                v-model="page"
                :total-rows="count"
                :per-page="pageSizes.selected"
                first-number
                last-number
                @change="handlePageChange"
                aria-controls="reviews-list"
            ></b-pagination>
          </div>
        </div>
      </div>

    </div>
    <b-sidebar id="sidebar-right" right v-model="isUserPaneDisplayed">
      <vue-tabs
          v-model="userTabsActive"
          active-tab-color="#213213"
          active-text-color="white">
        <v-tab v-for="userTab in userTabs" :key="userTab.userId" :title="userTab.userId" >
          <div slot="title">{{ userTab.name }} <i @click.stop="removeUserTab(userTab.userId)" class="bx bx-x-circle" ></i></div>
          <UserProfile
              :userId="null"
              :user="userTab"
              v-on:SHOW_MEDIA="showUserMedia">
          </UserProfile>
        </v-tab>
      </vue-tabs>
    </b-sidebar>

    <vue-simple-context-menu
        element-id="reviewsContextMenu"
        :options="optionsContext"
        ref="reviewsContextMenu"
        @option-clicked="processContextMenu"
    />

    <CoolLightBox
        :items="userMedia.media"
        :index="userMedia.index"
        :effect="'fade'"
        @close="userMedia.index = null">
    </CoolLightBox>

  </Layout>
</template>

<style>
.b-sidebar-right {
  width:35%;
}

.b-sidebar-body {
  padding:16px;
}
</style>
